/*
 * Cart drawer — subscription upgrade and frequency change.
 *
 * Listens for clicks/changes on the cart drawer's subscription affordances:
 *   [data-cart-sub-upgrade]  — full-width "Upgrade to subscription" button on
 *                              one-time line items. POSTs to /cart/change.js
 *                              with selling_plan: the button's
 *                              data-selling-plan. cart-drawer.liquid picks
 *                              that plan (the variant's "Every 2 months" plan
 *                              when it has one, else its first); this script
 *                              never chooses a plan itself.
 *   [data-cart-sub-period]   — <select> on subscribed line items listing the
 *                              variant's frequencies plus "Switch to one-time"
 *                              (value ""; Shopify drops the plan). POSTs the
 *                              chosen plan id.
 *
 * Lines are targeted by line-item key (data-key → {id: key}), not by their
 * position, so a stale drawer can't change the wrong line; requests run
 * through the shared cart queue (window.ebCartMutex, assets/cart.js).
 *
 * Mirrors the section-rendering pattern used by CartDrawerItems in cart.js +
 * cart-drawer.js: requests sections=['cart-drawer','cart-icon-bubble'] and
 * swaps innerHTML of .drawer__inner and the cart bubble.
 *
 * Subscriptions are native Shopify subscription contracts (the
 * "Subscribe & Save" selling plan group). /cart/change.js's selling_plan
 * parameter targets the plan IDs from the variant's selling_plan_allocations
 * directly — no third-party app plumbing. (Migrated off Seal Subscriptions in
 * 2026-06.)
 */
(function () {
  'use strict';

  if (!window.routes || !window.routes.cart_change_url) return;

  var SECTIONS = [
    { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
  ];

  function getSectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /* Where focus belongs after a plan change re-renders the drawer. The line
     KEY changes with the plan, so the changed line is found by its position
     (data-line) and its variant — a key starts with "<variant id>:" — then
     anywhere by variant (a line merged into another), and within it the
     subscription control that replaced this one (the <select> after an
     upgrade, the button after "Switch to one-time"), else its name link.
     Captured at click/change time: disabling the control drops focus. */
  function focusContext(control) {
    var drawer = document.querySelector('cart-drawer');
    var hadFocus = !!(drawer && drawer.contains(document.activeElement));
    var variant = (control.dataset.key || '').split(':')[0];
    var line = parseInt(control.dataset.line, 10);
    var successors = [];
    if (/^\d+$/.test(variant)) {
      var byVariant = '.cart-item[data-key^="' + variant + ':"]';
      var rows = isNaN(line) ? [byVariant] : ['#CartDrawer-Item-' + line + byVariant, byVariant];
      rows.forEach(function (row) {
        successors.push(row + ' [data-cart-sub-period]', row + ' [data-cart-sub-upgrade]');
      });
      successors.push(byVariant + ' .cart-item__name');
    }
    return { hadFocus: hadFocus, successors: successors };
  }

  function refresh(parsedState, focus) {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
    }
    SECTIONS.forEach(function (s) {
      var html = parsedState.sections[s.section];
      if (!html) return;
      var target = document.getElementById(s.id);
      if (!target) return;
      var inner = target.querySelector(s.selector) || target;
      /* Preload images first so the cache is warm by the time the
         innerHTML swap lands — masks the brief cart-img flicker on
         qty change / subscription upgrade / period change. */
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var newSection = doc.querySelector(s.selector);
      if (newSection) {
        Array.prototype.forEach.call(
          newSection.querySelectorAll('img[src]'),
          function (img) { var i = new Image(); i.src = img.getAttribute('src'); }
        );
      }
      inner.innerHTML = getSectionInnerHTML(html, s.selector);
    });
    /* Notify pubsub subscribers (e.g. product-info quantity rules) and
       dispatch a CustomEvent for our own loosely-coupled listeners
       (cart-drawer-recommendations.js re-hydration). */
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-subscriptions',
          cartData: parsedState
        });
      }
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('cart-drawer:rerender', { detail: { source: 'subscriptions' } }));
    /* Re-trap focus on the new markup (cart-drawer.js). */
    if (focus && cartDrawer && typeof cartDrawer.refocusAfterRender === 'function') {
      cartDrawer.refocusAfterRender(focus.hadFocus, focus.successors);
    }
  }

  /* Resolves to the parsed response; rejects on any error shape Shopify
     uses (non-2xx, {status >= 400}, {errors}) or a response without the
     sections we asked for. */
  function changeLine(control, payload) {
    var body = Object.assign(
      {
        sections: SECTIONS.map(function (s) { return s.section; }),
        sections_url: window.location.pathname
      },
      control.dataset.key ? { id: control.dataset.key } : { line: parseInt(control.dataset.line, 10) },
      payload
    );
    var task = function () {
      return fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.text().then(function (t) {
          var json;
          try { json = JSON.parse(t); } catch (e) { json = null; }
          if (!r.ok || !json || json.status >= 400 || json.errors || !json.sections) {
            throw new Error((json && (json.description || json.message || json.errors)) || ('HTTP ' + r.status));
          }
          return json;
        });
      });
    };
    return window.ebCartMutex ? window.ebCartMutex(task) : task();
  }

  /* A failed change used to leave the control disabled and the <select>
     showing a frequency that was never applied, with the error only in the
     console. Restore the control, say so, and re-read the cart in case the
     drawer was stale (the usual cause of a rejected change). */
  function fail(control, err, label) {
    console.error('cart-drawer-subscriptions: ' + label + ' failed', err);
    if (control.isConnected) {
      control.disabled = false;
      if (control.tagName === 'SELECT') {
        Array.prototype.forEach.call(control.options, function (o) { o.selected = o.defaultSelected; });
      }
    }
    var errors = document.getElementById('CartDrawer-CartErrors');
    if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.resync === 'function') {
      drawer.resync({ force: true }).then(function (changed) {
        var fresh = document.getElementById('CartDrawer-CartErrors');
        if (changed && fresh && window.cartStrings) fresh.textContent = window.cartStrings.error;
      });
    }
  }

  function getLineQty(item) {
    if (!item) return 1;
    var input = item.querySelector('.quantity__input');
    if (!input) return 1;
    var v = parseInt(input.value, 10);
    return isNaN(v) || v < 1 ? 1 : v;
  }

  document.addEventListener('click', function (e) {
    var upgrade = e.target.closest('[data-cart-sub-upgrade]');
    if (!upgrade || upgrade.disabled) return;
    e.preventDefault();
    var item = upgrade.closest('.cart-item');
    var sellingPlan = upgrade.dataset.sellingPlan;
    var qty = getLineQty(item);
    var focus = focusContext(upgrade);
    upgrade.disabled = true;
    changeLine(upgrade, { quantity: qty, selling_plan: sellingPlan })
      .then(function (parsedState) { refresh(parsedState, focus); })
      .catch(function (err) { fail(upgrade, err, 'upgrade'); });
  });

  // Capture phase + stopPropagation: the period <select> lives inside
  // <cart-drawer-items>, whose Dawn `change` handler (cart.js onChange) would
  // otherwise treat this as a quantity change — calling updateQuantity() with
  // the selling-plan id as the "quantity" and an undefined line — firing a
  // failing /cart/change.js that flashes "There was an error while updating
  // your cart" before our correct change re-renders. Capturing first and
  // stopping propagation keeps Dawn's handler from ever seeing this event.
  // (Quantity inputs don't match the selector below, so they fall through to
  // Dawn untouched.)
  document.addEventListener('change', function (e) {
    var select = e.target.closest('[data-cart-sub-period]');
    if (!select || select.disabled) return;
    e.stopPropagation();
    var item = select.closest('.cart-item');
    var sellingPlan = select.value;
    var qty = getLineQty(item);
    var focus = focusContext(select);
    select.disabled = true;
    changeLine(select, { quantity: qty, selling_plan: sellingPlan })
      .then(function (parsedState) { refresh(parsedState, focus); })
      .catch(function (err) { fail(select, err, 'period change'); });
  }, true);
})();
